import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { extensions, dialogs, utils, i18next, linkEditing, integration } from '@yola/ws-sdk';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import ModalWithPanels from 'src/js/modules/common/components/modal-with-panels';
import IconInputField from 'src/js/modules/common/components/icon-input-field';
import segment from 'src/js/modules/analytics/segment';
import common from 'src/js/modules/common/constants/common';
import constants from '../constants';
import getCurrentItemOrder from '../helpers/get-current-item-order';
import validateUrl from '../helpers/validate-url';
import getPreloadedIcons from '../helpers/get-preloaded-icons';
import matchIconName from '../helpers/match-icon-name';
import normalizeUrl from '../helpers/normalize-url';
import getDefaultTraits from '../../../../analytics/segment/helpers/get-default-traits';
import generateRandomId from '../helpers/generate-random-id';
import getCurrentItemSize from '../helpers/get-current-item-size';

const {
  track,
  constants: { events },
} = segment;

const { InputGroupField, Box, Stack, ControlGroup } = designSystem;
const { useForm } = reactHookForm;
const { linkTypes } = linkEditing.constants;
const { BLANK } = linkEditing.constants.common;
const { URL_FIELD_NAME, ICON_EXTENSION_NAME, DIALOG_WIDTH, ICON_FIELD_NAME } = constants.common;

const getCaptions = () => ({
  title: i18next.t('Social link settings'),
  newItemTitle: i18next.t('New item'),
  submitButton: i18next.t('Submit'),
  cancelButton: i18next.t('Cancel'),
  iconInputButton: i18next.t('Choose icon'),
  urlLabel: i18next.t('Link URL'),
  iconLabel: i18next.t('Link Icon'),
  errors: {
    emptyIcon: i18next.t('No icon chosen'),
  },
});

const updateItem = (items, id, iconSrc, linkUrl, iconBaseSrc, iconIdAttr) =>
  items.map((currItem) =>
    currItem.id === id
      ? {
          ...currItem,
          iconIdAttr,
          iconSrc,
          iconBaseSrc,
          config: {
            ...currItem.config,
            reference: linkUrl,
          },
        }
      : currItem
  );

const getDefaultItem = (items) => ({
  id: generateRandomId(),
  order: getCurrentItemOrder(items),
  config: {
    linkType: linkTypes.EXTERNAL,
    target: BLANK,
    reference: '',
  },
  iconSrc: '',
  iconBaseSrc: '',
  size: getCurrentItemSize(items),
});

const ItemSettingsDialog = ({
  elementId = '',
  targetLink = null,
  getSharedData = utils.noop,
  resolveSharedData = utils.noop,
}) => {
  const id = targetLink && targetLink.id;
  const {
    offsetX: dialogOffsetX,
    offsetY: dialogOffsetY,
    items,
    currentItem,
    isTouched,
    initialItemUrl,
    initialItemIconSrc,
  } = getSharedData();
  const [dialogPosition, setDialogPosition] = useState({ x: dialogOffsetX, y: dialogOffsetY });
  const userData = useSelector(integration.selectors.getUserData);
  const isB2C = !userData.is_white_label;

  const item = currentItem || targetLink || getDefaultItem(items);
  const memoizedIcons = useMemo(getPreloadedIcons, []);
  const captions = getCaptions();

  const {
    watch,
    setValue,
    handleSubmit,
    control: fieldControl,
  } = useForm({
    defaultValues: {
      [URL_FIELD_NAME]: item.config.reference || '',
      [ICON_FIELD_NAME]: item.iconSrc || '',
    },
  });

  useEffect(() => {
    resolveSharedData({
      initialItemUrl: typeof initialItemUrl === 'string' ? initialItemUrl : item.config.reference,
      initialItemIconSrc:
        typeof initialItemIconSrc === 'string' ? initialItemIconSrc : item.iconSrc,
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const iconAutocomplete = (url) => {
    if (isTouched) return;

    if (item.iconSrc) {
      const iconFileName = item.iconSrc.split('/').pop();
      const nextIconName = matchIconName(url);

      if (iconFileName.includes(nextIconName)) return;
    }

    const foundIconName = matchIconName(url);

    if (!foundIconName) return;

    const icon = memoizedIcons[foundIconName];
    setValue(ICON_FIELD_NAME, icon.src);

    resolveSharedData({
      currentItem: {
        ...item,
        iconSrc: icon.src,
        iconBaseSrc: null,
      },
    });
  };

  const iconSrc = item.iconSrc || '';
  const linkUrl = watch(URL_FIELD_NAME);

  const updateSharedData = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.x,
      offsetY: dialogPosition.y,
      ...customData,
    });
  };

  const closeDialog = () => {
    updateSharedData({
      currentItem: null,
      isTouched: false,
      initialItemUrl: null,
      initialItemIconSrc: null,
    });

    dialogs.operations.show(dialogTypes.SOCIAL_LINKS_SETTINGS_DIALOG, { elementId, items });

    const eventName = id
      ? events.SOCIAL_LINKS_EDIT_LINK_DIALOG_CANCELED
      : events.SOCIAL_LINKS_ADD_LINK_DIALOG_CANCELED;

    track(eventName, {
      ...getDefaultTraits(elementId),
      dialogId: dialogTypes.SOCIAL_LINKS_ITEM_SETTINGS_DIALOG,
    });
  };

  const openIconExtension = () => {
    const extension = extensions.accessors.getExtension(ICON_EXTENSION_NAME);
    const control = extension.ui.control[0];

    const onDialogCancel = (newOffsetX, newOffsetY) => {
      resolveSharedData({
        offsetX: newOffsetX,
        offsetY: newOffsetY,
        currentItem: {
          ...item,
          config: {
            ...item.config,
            reference: linkUrl,
          },
        },
        isTouched,
      });

      dialogs.operations.show(dialogTypes.SOCIAL_LINKS_ITEM_SETTINGS_DIALOG, {
        elementId,
        targetLink,
      });
    };

    const onSubmit = ({ newIconSrc, newIconBaseSrc, newOffsetX, newOffsetY, iconIdAttr }) => {
      resolveSharedData({
        offsetX: newOffsetX,
        offsetY: newOffsetY,
        currentItem: {
          ...item,
          config: {
            ...item.config,
            reference: linkUrl,
          },
          iconSrc: newIconSrc,
          iconBaseSrc: newIconBaseSrc,
          iconIdAttr,
        },
        isTouched: true,
      });

      dialogs.operations.show(dialogTypes.SOCIAL_LINKS_ITEM_SETTINGS_DIALOG, {
        elementId,
        targetLink,
      });
    };

    control.openExtension({
      offsetX: dialogPosition.x,
      offsetY: dialogPosition.y,
      iconSrc,
      onDialogCancel,
      onSubmit,
      elementId,
      iconIdAttr: item.iconIdAttr,
    });
  };

  const onSubmit = async () => {
    const socialLink = { ...item };

    if (!socialLink.iconBaseSrc) {
      const {
        api: { copyExtensionAsset },
      } = extensions.accessors.getExtension(ICON_EXTENSION_NAME);
      socialLink.iconBaseSrc = await copyExtensionAsset(socialLink.iconSrc);
    }

    const url = normalizeUrl(linkUrl);

    const updatedItems = id
      ? updateItem(items, id, iconSrc, url, socialLink.iconBaseSrc, socialLink.iconIdAttr)
      : [...items, { ...socialLink, config: { ...socialLink.config, reference: url } }];

    if (id) {
      track(events.SOCIAL_LINKS_EDIT_LINK_DIALOG_SUBMITTED, {
        ...getDefaultTraits(elementId),
        dialogId: dialogTypes.SOCIAL_LINKS_ITEM_SETTINGS_DIALOG,
        iconSelected: initialItemIconSrc !== iconSrc,
        urlChanged: initialItemUrl !== linkUrl,
      });
    } else {
      track(events.SOCIAL_LINKS_ADD_LINK_DIALOG_SUBMITTED, {
        ...getDefaultTraits(elementId),
        dialogId: dialogTypes.SOCIAL_LINKS_ITEM_SETTINGS_DIALOG,
        iconSelected: initialItemIconSrc !== iconSrc,
        urlChanged: true,
      });
    }

    updateSharedData({
      items: updatedItems,
      currentItem: null,
      isTouched: false,
      initialItemUrl: null,
      initialItemIconSrc: null,
    });

    dialogs.operations.show(dialogTypes.SOCIAL_LINKS_SETTINGS_DIALOG, {
      elementId,
      items: updatedItems,
    });
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === common.ENTER_KEYCODE) {
      handleSubmit(onSubmit);
    }
  };

  const handleLinkUrlChange = (value) => {
    iconAutocomplete(value);
  };

  const validateUrlField = (value) => validateUrl(value);

  const validateIconField = (value) => {
    if (value || iconSrc) {
      return null;
    }

    return captions.iconInputButton;
  };

  const handleDialogDragEnd = (event, position) => {
    setDialogPosition(position);
  };

  return (
    <ModalWithPanels
      width={DIALOG_WIDTH}
      height="auto"
      captions={{
        title: id ? captions.title : captions.newItemTitle,
        submitButton: captions.submitButton,
        cancelButton: captions.cancelButton,
      }}
      onSubmit={handleSubmit(onSubmit)}
      onCancel={closeDialog}
      onOverlayClick={handleSubmit(onSubmit)}
      onPressEsc={closeDialog}
      offsetY={dialogPosition.y}
      offsetX={dialogPosition.x}
      onKeyDown={handleKeyDown}
      onDragEnd={handleDialogDragEnd}
    >
      <Box paddingTop="spacing-3-xs" paddigBottom="spacing-3-xs">
        <Stack gap="spacing-m">
          <ControlGroup title={captions.urlLabel}>
            <InputGroupField
              control={fieldControl}
              name={URL_FIELD_NAME}
              placeholder="http://"
              rules={{ validate: validateUrlField }}
              onChange={handleLinkUrlChange}
            />
          </ControlGroup>
          <ControlGroup title={captions.iconLabel}>
            <IconInputField
              name={ICON_FIELD_NAME}
              control={fieldControl}
              captions={{
                button: captions.iconInputButton,
                empty: captions.errors.emptyIcon,
              }}
              onClick={openIconExtension}
              rules={{ validate: validateIconField }}
            />
          </ControlGroup>
        </Stack>
      </Box>
    </ModalWithPanels>
  );
};

ItemSettingsDialog.propTypes = {
  elementId: PropTypes.string,
  targetLink: PropTypes.shape({
    id: PropTypes.string,
  }),
  getSharedData: PropTypes.func,
  resolveSharedData: PropTypes.func,
};

export default ItemSettingsDialog;
